import { SkuFilter, TotalSKUSizeRange } from '@emi/shared/models';
import { Category } from '../category';
import { Filters } from '../filters';
import { SkuField } from '../types/sku-field.type';
import { convertMetric } from '@emi/shared/utils';
import { DateRange } from '../date-range';
import * as moment from 'moment';
import * as Constants from '@emi/shared/constants';

export class SkuSearchRequest {
    constructor(
        public filters: SkuFilter,
        public searchType: string = 'all',
        public searchPhrase?: string,
        public fields: Array<SkuField> = [],
        public currency?: string,
        public pageId?: string,
        public resultsPerPage?: number,
        public sortBy?: string,
        public orderBy?: string,
    ) { }


    public static from({
        filters,
        searchPhrase,
        searchType,
        fields,
        currency,
        pageId,
        resultsPerPage,
        sortBy,
        orderBy
    }: {
        filters: Filters;
        searchPhrase?: string;
        searchType?: string;
        fields?: Array<SkuField>;
        currency?: string;
        pageId?: string;
        resultsPerPage?: number;
        sortBy?: string;
        orderBy?: string;
    }): SkuSearchRequest {
        const skuFilters = new SkuFilter();

        skuFilters.attributeGroupIds = filters.attributeTypes.map((x) => x.id);
        skuFilters.attributeIds = filters.attributes.map((x) => x.id);
        skuFilters.countryIds = filters.countries.map((x) => x.id);
        skuFilters.retailerTypeIds = filters.retailerTypes.map((x) => x.id);
        skuFilters.retailerIds = filters.retailers.map((x) => x.id);
        skuFilters.supplierIds = filters.suppliers.map((x) => x.id);
        skuFilters.promotionTypeIds = filters.promotions.map((x) => x.id);

        this.buildCategoryIds(filters.categories, skuFilters.categoryIds);

        filters.brands.forEach((brand) => {
          brand?.subBrands?.forEach((subBrand) => {
              skuFilters.subBrandIds.push(subBrand.subBrandId)
          });
          if(brand.isParentBrandSelected === true)
          {
            skuFilters.brandIds.push(brand.brandId);
          }
        });

        skuFilters.firstSeenPriceRange = filters.firstSeenPriceRange;
        skuFilters.lastSeenPriceRange = filters.lastSeenPriceRange;
        skuFilters.dateRange = filters.dateRange;
        
        // Bug 309020: Via V2 | Count mismatch on search vs full date range search 
        // NOTE: This might get removed if we decide to update the migration scripts instead.
        if (!skuFilters?.dateRange?.startDate || !skuFilters?.dateRange?.endDate) {
            skuFilters.dateRange = new DateRange(
                moment().utc().subtract(Constants.DatePicker.MAX_ALLOWED_YEARS_AGO, 'years').startOf('year').toDate(),
                new Date()
            );
        }
        skuFilters.volumeRange = this.prepareVolumeRangeForTotalSKUSizeFilter(filters.volumeRange);
        skuFilters.weightRange = this.prepareWeightRangeForTotalSKUSizeFilter(filters.weightRange);
        skuFilters.countRange = filters.countRange;

        return new SkuSearchRequest(skuFilters, searchType, searchPhrase, fields, currency, pageId, resultsPerPage, sortBy, orderBy);
    }

    private static buildCategoryIds(categoryTree: Array<Category>, categoryIds: Array<number>): void {
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                categoryIds.push(category.id);
            } else {
                this.buildCategoryIds(category.childCategories, categoryIds);
            }
        }
    }

    private static prepareVolumeRangeForTotalSKUSizeFilter(volumeRange: TotalSKUSizeRange | null): TotalSKUSizeRange | null {
        if (
            volumeRange &&
            !this.isNullOrUndefined(volumeRange.min) &&
            !this.isNullOrUndefined(volumeRange.max) &&
            volumeRange.measureOptions
        ) {
            if (volumeRange.measureOptions !== 'ml') {
                const convertedFrom = convertMetric(Number(volumeRange.min), volumeRange.measureOptions, 'ml');
                const convertedTo = convertMetric(Number(volumeRange.max), volumeRange.measureOptions, 'ml');
                volumeRange = new TotalSKUSizeRange(volumeRange.isSelected, volumeRange.measureOptions, convertedFrom, convertedTo);
            }
        }
        return volumeRange;
    }

    private static prepareWeightRangeForTotalSKUSizeFilter(weightRange: TotalSKUSizeRange | null): TotalSKUSizeRange | null {
        if (
            weightRange &&
            !this.isNullOrUndefined(weightRange.min) &&
            !this.isNullOrUndefined(weightRange.max) &&
            weightRange.measureOptions
        ) {
            if (weightRange.measureOptions !== 'g') {
                const convertedFrom = convertMetric(Number(weightRange.min), weightRange.measureOptions, 'g');
                const convertedTo = convertMetric(Number(weightRange.max), weightRange.measureOptions, 'g');
                weightRange = new TotalSKUSizeRange(weightRange.isSelected, weightRange.measureOptions, convertedFrom, convertedTo);
            }
        }
        return weightRange;
    }

    private static isNullOrUndefined(value: any): boolean {
        if (value === null || value === undefined) {
            return true;
        } else {
            return false;
        }
    }
}
